/**
 * Webhooks Admin Page
 * ===================
 * Two tabs: Endpoints (CRUD) + Deliveries (log + retry).
 * - Endpoint creation reveals the signing secret EXACTLY ONCE; admin must copy it.
 * - "Send Test Ping" delivers a synthetic event so the integration can be sanity-checked.
 * - Deliveries shows attempt count, signed status, response code, and a manual retry button.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Webhook, Plus, Trash2, Loader2, X, Copy, Check, RefreshCw, Send,
  ListChecks, KeyRound, CheckCircle2, AlertTriangle, Clock,
  Power, PowerOff, RotateCw,
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/webhooks`;

const STATUS_COLORS = {
  delivered: "#22c55e", pending: "#f59e0b", failed_permanent: "#ef4444",
  cancelled: "#94a3b8",
};

const inputStyle = { width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 12px", color: "#f1f5f9", fontSize: 12 };
const btnPrimary = { background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: 6, color: "white", padding: "8px 18px", fontWeight: 600, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 };
const btnGhost = { background: "transparent", border: "1px solid #1e2530", borderRadius: 6, color: "#94a3b8", padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 };

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: "#0d1117", border: `1px solid ${color}33`, borderRadius: 10, padding: 14 }}>
      <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontSize: 24, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function CopyBtn({ text, label = "Copy", testId }) {
  const [done, setDone] = useState(false);
  const onClick = async () => {
    try { await navigator.clipboard.writeText(text); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1500); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={onClick} data-testid={testId} style={{ ...btnGhost, padding: "6px 10px", color: done ? "#22c55e" : "#2563eb", borderColor: done ? "#22c55e" : "#2563eb" }}>
      {done ? <Check size={11} /> : <Copy size={11} />} {done ? "Copied" : label}
    </button>
  );
}

// =====================================================
// ============== CREATE / SECRET MODAL ================
// =====================================================
function CreateModal({ allEvents, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", url: "", events: ["watchlist.alert_raised", "case.created"] });
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(null);

  const toggle = (ev) => setForm((f) => ({ ...f, events: f.events.includes(ev) ? f.events.filter((e) => e !== ev) : [...f.events, ev] }));

  const submit = async () => {
    if (!form.name.trim() || !form.url.trim()) { toast.error("Name and URL are required"); return; }
    if (!form.events.length) { toast.error("Select at least one event"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/endpoints`, form, { withCredentials: true });
      setShowSecret(data.secret_show_once);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 24, width: "100%", maxWidth: 620 }} data-testid="create-webhook-modal">
        {showSecret ? (
          <>
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={18} style={{ color: "#facc15" }} />
              <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, margin: 0 }}>Signing Secret — Copy Now</h3>
            </div>
            <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 14 }}>
              This is the only time we'll show this secret. Store it securely on your side — you'll use it to verify the
              <code style={{ color: "#cbd5e1", background: "#080c12", padding: "1px 6px", borderRadius: 4, margin: "0 4px", fontSize: 11 }}>X-Rudrik-Signature</code>
              header on every webhook delivery.
            </p>
            <div style={{ background: "#080c12", border: "1px solid #facc15", borderRadius: 8, padding: 14, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <code style={{ color: "#facc15", fontFamily: "ui-monospace, monospace", fontSize: 12, wordBreak: "break-all" }} data-testid="webhook-secret-shown">{showSecret}</code>
              <CopyBtn text={showSecret} testId="copy-secret-btn" />
            </div>
            <div style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: 12, fontSize: 11, color: "#94a3b8", marginBottom: 14, lineHeight: 1.6 }}>
              <strong style={{ color: "#cbd5e1" }}>Verification recipe</strong> — concatenate
              <code style={{ color: "#cbd5e1" }}>{` <timestamp>.<body> `}</code>
              then HMAC-SHA256 with this secret. Compare against the
              <code style={{ color: "#cbd5e1" }}>{` X-Rudrik-Signature `}</code>
              header (prefixed with <code style={{ color: "#cbd5e1" }}>sha256=</code>).
            </div>
            <div className="flex items-center justify-end">
              <button onClick={onClose} style={btnPrimary} data-testid="secret-close-btn"><Check size={12} /> I've Saved It</button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, margin: 0 }}>Add Webhook Endpoint</h3>
              <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Name</div>
                <input data-testid="webhook-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} placeholder="e.g. Case Management Slack Bot" />
              </div>
              <div>
                <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>URL (HTTPS recommended)</div>
                <input data-testid="webhook-url" type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} style={inputStyle} placeholder="https://example.com/webhooks/rudrik" />
              </div>
              <div>
                <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Subscribe to Events</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {allEvents.map((ev) => (
                    <label key={ev} style={{ display: "flex", alignItems: "center", gap: 8, background: form.events.includes(ev) ? "#1e3a5f33" : "#080c12", border: `1px solid ${form.events.includes(ev) ? "#2563eb" : "#1e2530"}`, borderRadius: 6, padding: "8px 10px", cursor: "pointer", fontSize: 12, color: form.events.includes(ev) ? "#f1f5f9" : "#94a3b8" }} data-testid={`event-${ev}`}>
                      <input type="checkbox" checked={form.events.includes(ev)} onChange={() => toggle(ev)} style={{ accentColor: "#2563eb" }} />
                      <code style={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>{ev}</code>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={onClose} style={btnGhost}>Cancel</button>
              <button onClick={submit} disabled={saving} data-testid="create-webhook-submit" style={btnPrimary}>
                {saving ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />}
                {saving ? "Creating..." : "Create Endpoint"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =====================================================
// ============== ENDPOINTS TAB ========================
// =====================================================
function EndpointsTab({ endpoints, load, allEvents, onShowSecret }) {
  const [showCreate, setShowCreate] = useState(false);

  const toggleActive = async (ep) => {
    try { await axios.patch(`${API}/endpoints/${ep.id}`, { is_active: !ep.is_active }, { withCredentials: true }); toast.success(ep.is_active ? "Paused" : "Activated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const test = async (ep) => {
    try {
      const { data } = await axios.post(`${API}/endpoints/${ep.id}/test`, {}, { withCredentials: true });
      data.ok ? toast.success(`Test delivered (HTTP ${data.response_status})`) : toast.error(`Test failed: ${data.error || data.response_status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  const rotate = async (ep) => {
    if (!window.confirm("Rotate signing secret? The current one will stop working immediately.")) return;
    try {
      const { data } = await axios.post(`${API}/endpoints/${ep.id}/rotate-secret`, {}, { withCredentials: true });
      onShowSecret(data.secret_show_once);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (ep) => {
    if (!window.confirm(`Delete webhook "${ep.name}"?`)) return;
    try { await axios.delete(`${API}/endpoints/${ep.id}`, { withCredentials: true }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <div className="flex items-center justify-end mb-3">
        <button data-testid="new-webhook-btn" onClick={() => setShowCreate(true)} style={btnPrimary}><Plus size={12} /> New Endpoint</button>
      </div>
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#080c12" }}>
            <Th>Status</Th><Th>Name</Th><Th>URL</Th><Th>Events</Th><Th>Secret</Th><Th>Created</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {endpoints.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#64748b", fontSize: 13 }}>
                No webhook endpoints yet. Click <strong style={{ color: "#2563eb" }}>"New Endpoint"</strong> to wire up Slack / Teams / your CRM / Zapier / n8n.
              </td></tr>
            ) : endpoints.map((ep) => (
              <tr key={ep.id} style={{ borderTop: "1px solid #1e2530" }} data-testid={`endpoint-row-${ep.id}`}>
                <Td>{ep.is_active
                  ? <span style={{ background: "#22c55e22", color: "#22c55e", border: "1px solid #22c55e55", fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase" }}>Active</span>
                  : <span style={{ background: "#94a3b822", color: "#94a3b8", border: "1px solid #94a3b855", fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase" }}>Paused</span>}
                </Td>
                <Td><strong style={{ color: "#f1f5f9", fontSize: 12 }}>{ep.name}</strong></Td>
                <Td><code style={{ color: "#94a3b8", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{ep.url}</code></Td>
                <Td>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {(ep.events || []).map((e) => <code key={e} style={{ background: "#1e3a5f33", color: "#3b82f6", border: "1px solid #2563eb55", borderRadius: 4, padding: "1px 6px", fontSize: 9, fontFamily: "ui-monospace, monospace" }}>{e}</code>)}
                  </div>
                </Td>
                <Td><code style={{ color: "#64748b", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{ep.secret_preview || "—"}</code></Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 11 }}>{new Date(ep.created_at).toLocaleDateString()}</span></Td>
                <Td>
                  <div className="flex gap-1">
                    <button onClick={() => test(ep)} title="Send test ping" data-testid={`test-${ep.id}`} style={{ background: "rgba(34,197,94,0.10)", border: "1px solid #22c55e", borderRadius: 4, color: "#22c55e", padding: "4px 6px", fontSize: 10, cursor: "pointer" }}><Send size={11} /></button>
                    <button onClick={() => toggleActive(ep)} title={ep.is_active ? "Pause" : "Activate"} data-testid={`toggle-${ep.id}`} style={{ background: "rgba(245,158,11,0.10)", border: "1px solid #f59e0b", borderRadius: 4, color: "#f59e0b", padding: "4px 6px", fontSize: 10, cursor: "pointer" }}>{ep.is_active ? <PowerOff size={11} /> : <Power size={11} />}</button>
                    <button onClick={() => rotate(ep)} title="Rotate secret" data-testid={`rotate-${ep.id}`} style={{ background: "rgba(168,85,247,0.10)", border: "1px solid #a855f7", borderRadius: 4, color: "#a855f7", padding: "4px 6px", fontSize: 10, cursor: "pointer" }}><RotateCw size={11} /></button>
                    <button onClick={() => remove(ep)} title="Delete" data-testid={`delete-${ep.id}`} style={{ background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", borderRadius: 4, color: "#ef4444", padding: "4px 6px", fontSize: 10, cursor: "pointer" }}><Trash2 size={11} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCreate && <CreateModal allEvents={allEvents} onClose={() => setShowCreate(false)} onCreated={load} />}
    </>
  );
}

// =====================================================
// ============== DELIVERIES TAB =======================
// =====================================================
function DeliveriesTab() {
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState("");
  const [eventF, setEventF] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [allEvents, setAllEvents] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/event-types`, { withCredentials: true }); setAllEvents(data.events || []); }
      catch (err) { console.error("[Webhooks] Failed to load event types:", err); }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusF) params.status = statusF;
      if (eventF) params.event_type = eventF;
      const { data } = await axios.get(`${API}/deliveries`, { params, withCredentials: true });
      setItems(data.deliveries || []);
    } catch (e) { toast.error("Failed to load deliveries"); }
    finally { setLoading(false); }
  }, [statusF, eventF]);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    try { await axios.post(`${API}/deliveries/${id}/retry`, {}, { withCredentials: true }); toast.success("Re-queued"); setTimeout(load, 1000); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <div className="flex items-center gap-3">
          <ListChecks size={14} style={{ color: "#94a3b8" }} />
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="delivery-status-filter" style={{ ...inputStyle, width: 180 }}>
            <option value="">All statuses</option>
            <option value="delivered">Delivered</option>
            <option value="pending">Pending / Retrying</option>
            <option value="failed_permanent">Failed (no retries left)</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={eventF} onChange={(e) => setEventF(e.target.value)} data-testid="delivery-event-filter" style={{ ...inputStyle, width: 240 }}>
            <option value="">All events</option>
            {allEvents.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
          </select>
          <button onClick={load} style={btnGhost}><RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh</button>
        </div>
      </div>

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#080c12" }}>
            <Th>Status</Th><Th>Event</Th><Th>Endpoint</Th><Th>Response</Th><Th>Attempts</Th><Th>When</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#64748b", fontSize: 13 }}>No deliveries yet. Trigger an event to see them flow here.</td></tr>
            ) : items.map((d) => {
              const c = STATUS_COLORS[d.status] || "#64748b";
              const Icon = d.status === "delivered" ? CheckCircle2 : (d.status === "pending" ? Clock : AlertTriangle);
              return (
                <React.Fragment key={d.id}>
                  <tr style={{ borderTop: "1px solid #1e2530", cursor: "pointer" }} onClick={() => setExpanded(expanded === d.id ? null : d.id)} data-testid={`delivery-row-${d.id}`}>
                    <Td>
                      <span style={{ background: `${c}22`, color: c, border: `1px solid ${c}55`, fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Icon size={10} /> {d.status.replace("_", " ")}
                      </span>
                    </Td>
                    <Td><code style={{ background: "#1e3a5f33", color: "#3b82f6", border: "1px solid #2563eb55", borderRadius: 4, padding: "1px 6px", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{d.event_type}</code></Td>
                    <Td><span style={{ color: "#cbd5e1", fontSize: 11 }}>{d.endpoint_name}</span></Td>
                    <Td>{d.response_status ? <span style={{ color: d.response_status < 300 ? "#22c55e" : "#ef4444", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>HTTP {d.response_status}</span> : <span style={{ color: "#64748b", fontSize: 11 }}>—</span>}</Td>
                    <Td><strong style={{ color: d.attempts > 1 ? "#f59e0b" : "#cbd5e1", fontSize: 12 }}>{d.attempts}</strong></Td>
                    <Td><span style={{ color: "#94a3b8", fontSize: 11 }}>{new Date(d.last_attempt_at || d.created_at).toLocaleString()}</span></Td>
                    <Td>
                      {(d.status === "failed_permanent" || (d.status === "pending" && d.attempts > 0)) && (
                        <button onClick={(e) => { e.stopPropagation(); retry(d.id); }} data-testid={`retry-${d.id}`} title="Retry now" style={{ background: "rgba(168,85,247,0.10)", border: "1px solid #a855f7", borderRadius: 4, color: "#a855f7", padding: "4px 6px", fontSize: 10, cursor: "pointer" }}><RotateCw size={11} /></button>
                      )}
                    </Td>
                  </tr>
                  {expanded === d.id && (
                    <tr><td colSpan={7} style={{ background: "#080c12", padding: 16, borderTop: "1px solid #1e2530" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <div>
                          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Payload Sent</div>
                          <pre style={{ background: "#0d1117", color: "#cbd5e1", padding: 12, borderRadius: 6, fontSize: 10, overflowX: "auto", maxHeight: 240, margin: 0 }}>{JSON.stringify(d.payload, null, 2)}</pre>
                        </div>
                        <div>
                          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Response / Error</div>
                          <pre style={{ background: "#0d1117", color: d.error ? "#ef4444" : "#22c55e", padding: 12, borderRadius: 6, fontSize: 10, overflowX: "auto", maxHeight: 240, margin: 0 }}>{d.error || d.response_body || "(empty)"}</pre>
                          {d.next_retry_at && <div style={{ color: "#f59e0b", fontSize: 11, marginTop: 8 }}>Next retry at {new Date(d.next_retry_at).toLocaleString()}</div>}
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =====================================================
// ============== ROOT PAGE ============================
// =====================================================
export default function WebhooksPage() {
  const [tab, setTab] = useState("endpoints");
  const [endpoints, setEndpoints] = useState([]);
  const [stats, setStats] = useState({});
  const [allEvents, setAllEvents] = useState([]);
  const [secretShown, setSecretShown] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, s, ev] = await Promise.all([
        axios.get(`${API}/endpoints`, { withCredentials: true }),
        axios.get(`${API}/deliveries/stats`, { withCredentials: true }),
        axios.get(`${API}/event-types`, { withCredentials: true }),
      ]);
      setEndpoints(e.data.endpoints || []);
      setStats(s.data || {});
      setAllEvents(ev.data.events || []);
    } catch (err) { console.error("[Webhooks] Failed to load endpoints/stats:", err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { id: "endpoints",  label: "Endpoints",  icon: Webhook, count: endpoints.length },
    { id: "deliveries", label: "Deliveries", icon: ListChecks, count: stats.total || 0 },
  ];

  return (
    <div data-testid="webhooks-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Webhook style={{ width: 26, height: 26, color: "#2563eb" }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">Webhooks</h1>
        </div>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 18 }}>
        Forward Rudrik compliance events (case-created, watchlist match, SAR filed) to your downstream systems —
        HMAC-SHA256 signed, exponential-backoff retries (1m → 5m → 30m → 2h → 12h).
      </p>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 18 }}>
        <StatCard label="Active Endpoints" value={stats.endpoints || 0} color="#2563eb" />
        <StatCard label="Deliveries (Total)" value={stats.total || 0} color="#06b6d4" />
        <StatCard label="Delivered" value={stats.delivered || 0} color="#22c55e" />
        <StatCard label="Pending / Retrying" value={stats.pending || 0} color="#f59e0b" />
        <StatCard label="Failed" value={stats.failed_permanent || 0} color="#ef4444" />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #1e2530", marginBottom: 18 }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`wh-tab-${t.id}`}
              style={{
                background: active ? "#0d1117" : "transparent", color: active ? "#f1f5f9" : "#94a3b8",
                border: "none", borderBottom: active ? "2px solid #2563eb" : "2px solid transparent",
                padding: "10px 18px", fontWeight: active ? 700 : 500, fontSize: 13, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
              }}>
              <Icon size={14} /> {t.label}
              {t.count != null && t.count > 0 && (
                <span style={{ background: active ? "#2563eb" : "#1e2530", color: active ? "white" : "#94a3b8", borderRadius: 10, padding: "0 8px", fontSize: 10, fontWeight: 700 }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "endpoints"  && <EndpointsTab endpoints={endpoints} load={load} allEvents={allEvents} onShowSecret={setSecretShown} />}
      {tab === "deliveries" && <DeliveriesTab />}

      {/* Rotation reveal modal */}
      {secretShown && (
        <div onClick={() => setSecretShown(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 24, width: "100%", maxWidth: 620 }}>
            <div className="flex items-center gap-2 mb-3"><KeyRound size={18} style={{ color: "#facc15" }} /><h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, margin: 0 }}>New Secret — Copy Now</h3></div>
            <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 14 }}>The previous secret has been invalidated.</p>
            <div style={{ background: "#080c12", border: "1px solid #facc15", borderRadius: 8, padding: 14, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <code style={{ color: "#facc15", fontFamily: "ui-monospace, monospace", fontSize: 12, wordBreak: "break-all" }}>{secretShown}</code>
              <CopyBtn text={secretShown} testId="copy-rotated-secret" />
            </div>
            <div className="flex items-center justify-end"><button onClick={() => setSecretShown(null)} style={btnPrimary}><Check size={12} /> Done</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children }) { return <th style={{ textAlign: "left", padding: "10px 12px", color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{children}</th>; }
function Td({ children }) { return <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{children}</td>; }
